import React, { useEffect, useState } from "react";
import { routes } from "../../../../../constants";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CameraIcon, DropDownCloseIcon } from "../../../../../assets/svg";
import {
  disableOnWheel,
  getPhoneInputFilterOptions,
  sanitizeDataTypeClientSide,
  validatorFunctionToAllowOnlyEnglish,
} from "../../../../../utils/utils";
import { Form, Input, Typography, Button, Select, Flex, Row, Col } from "antd";
import {
  fileImageUpload,
  getCityAction,
  getCountryAction,
  getStateAction,
  getUserProfileAction,
  updateProviderProfileAction,
} from "../../../../../redux/services/auth/authServices";
import { baseRouteMaker } from "../../../../../constants/routeConsts";
import {
  cleanupCityList,
  cleanupStateList,
} from "../../../../../redux/reducers/authReducer";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import logo from "../../../../../assets/images/logo.png";
import LanguageDropdown from "../../../../common/language-dropdown/LanguageDropdown";
import { FileAttachment } from "../../../../common/fileAttachment";
import { useRef } from "react";
import { colors } from "../../../../../constants/colors";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { LeftOutlined } from "@ant-design/icons";
import { appConfig } from "../../../../../constants/config/appConfig";
import { CameraOutlined } from "@ant-design/icons";
import styles from "./ProfileSetup.module.scss";
import CountryPhoneInput from "antd-country-phone-input";
import {
  allowAlphaNumericCharacters,
  allowCharactersOnlyForDecimalNumbers,
  allowCharactersOnlyForIntegerNumbers,
} from "../../../../../utils/appKeyStrokeLimitingUtils";
import Spinner from "../../../../common/spinner/Spinner";

const { Title } = Typography;

const UpdateProfileSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { loginDetails, countryList, stateList, cityList } = useSelector(
    (state) => state?.auth
  );
  /* local storage dependencies */
  const { useSession, setUserSession } = useLocalStorage(
    appConfig.SESSION_USER_INFO
  );
  /* local storage dependencies */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [imageLoader, setImageLoader] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  const [isCountrySelected, setIsCountrySelected] = useState(false);
  const [isStateSelected, setIsStateSelected] = useState(false);

  const [selectedCountryObj, setSelectedCountryObj] = useState(null);
  const [selectedStateObj, setSelectedStateObj] = useState(null);
  const [selectedCityObj, setSelectedCityObj] = useState(null);

  /* module dependencies for attachment - single */
  const [singleAttachmentObj, setSingleAttachmentObj] = useState(null);
  const [
    singleAttachmentSelectedImageUrl,
    setSingleAttachmentSelectedImageUrl,
  ] = useState("");
  const attachmentRef = useRef(null);
  /* module dependencies for attachment - single */

  /* on Image changed- single attachment */
  const onImageChanged = (e) => {
    const file = e.target.files[0];
    setSingleAttachmentObj(file);
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      let formDataBody = formData;
      setImageLoader(true);
      dispatch(fileImageUpload(formDataBody)).then((apiRes) => {
        const imageUrl = apiRes?.[0];
        setSingleAttachmentSelectedImageUrl(imageUrl);
        setImageLoader(false);
      });
    }
  };
  /* on Image changed- single attachment */

  // country api call
  useEffect(() => {
    dispatch(getCountryAction());
  }, [dispatch]);

  const handleCountryChange = (value, element) => {
    const selectedCountryObject = countryList?.find(
      (item) => item._id === value
    );
    setSelectedCountryObj(selectedCountryObject);
    if (selectedCountryObject) {
      let stateObject = {
        country_code: selectedCountryObject?.numeric_code,
      };
      dispatch(getStateAction(stateObject));
      dispatch(cleanupStateList());
      dispatch(cleanupCityList());
      setIsCountrySelected(true);
      form.resetFields(["state", "city"]);
    } else {
      setIsCountrySelected(false);
      form.resetFields(["state", "city"]);
    }
  };

  const handleStateChange = (value, element) => {
    const selectedStateObject = stateList?.find((item) => item.id === value);
    setSelectedStateObj(selectedStateObject);
    if (selectedStateObject) {
      let dataToBeSent = {
        stateCode: selectedStateObject?.id,
      };
      dispatch(getCityAction(dataToBeSent));
      dispatch(cleanupCityList());
      setIsStateSelected(true);
      form.resetFields(["city"]);
    }
    else {
      setIsStateSelected(false);
      form.resetFields(["city"]);
    }
  };

  const handleCityChange = (value, element) => {
    const selectedCityObject = cityList?.find(
      (item) => item.id === element?.value
    );
    setSelectedCityObj(selectedCityObject);
  };

  useEffect(() => {
    dispatch(getUserProfileAction(setUserSession)).then((apiRes) => {
      const userDetails = apiRes;
      if (
        userDetails &&
        userDetails?.organization_id &&
        userDetails?.organization_id?.address &&
        userDetails?.organization_id?.address?.country &&
        userDetails?.organization_id?.address?.country !== undefined &&
        userDetails?.organization_id?.address?.country !== null
      ) {
        if (userDetails?.organization_id?.address?.country?.numeric_code) {
          let stateObject = {
            country_code: Number(
              userDetails?.organization_id?.address?.country?.numeric_code
            ),
          };
          dispatch(getStateAction(stateObject));
        }

        if (userDetails?.organization_id?.address?.state?.id) {
          let cityBody = {
            stateCode: userDetails?.organization_id?.address?.state?.id,
          };
          dispatch(getCityAction(cityBody));
        }
        setIsCountrySelected(true);
        setIsStateSelected(true);

        form.setFieldsValue({
          name: userDetails?.organization_id?.name,
          designation: userDetails?.organization_id?.designation,
          branch: userDetails?.organization_id?.branch,
          director_name: userDetails?.organization_id?.director_name,
          email: userDetails?.email,
          phone_number: {
            phone: userDetails?.phone?.phone_number,
            code: Number(userDetails?.phone?.country_code),
          },
          address_line_1: userDetails?.organization_id?.address?.address_line_1,
          address_line_2: userDetails?.organization_id?.address?.address_line_2,
          country: userDetails?.organization_id?.address?.country?._id,
          zip_code: userDetails?.organization_id?.address?.zip_code,
          state: userDetails?.organization_id?.address?.state?.name,
          city: userDetails?.organization_id?.address?.city?.name,
          payment_type: userDetails?.organization_id?.payment_type,
          latitude: userDetails?.organization_id?.coordinates?.latitude,
          longitude: userDetails?.organization_id?.coordinates?.longitude,
        });
        setSelectedStateObj(userDetails?.organization_id?.address?.state);
        setSelectedCityObj(userDetails?.organization_id?.address?.city);
        setSelectedCountryObj(userDetails?.organization_id?.address?.country);
        setSingleAttachmentSelectedImageUrl(
          userDetails?.organization_id?.logo_image
        );
      }
    });
  }, []);

  /* on submit */
  const onSubmit = (values) => {
    let dataToBeSent = {
      name: values?.name,
      director_name: values?.director_name,
      branch: values?.branch,
      designation: values?.designation,
      email: values?.email,
      phone: {
        phone_number: values?.phone_number?.phone,
        country_code: values?.phone_number?.code,
      },
      address: {
        address_line_1: values?.address_line_1,
        address_line_2: values?.address_line_2,
        state: {
          id: selectedStateObj?.id,
          name: selectedStateObj?.name,
          state_code: selectedStateObj?.state_code,
        },
        city: {
          id: selectedCityObj?.id,
          name: selectedCityObj?.name,
        },
        zip_code: values?.zip_code,
        country: selectedCountryObj?._id,
      },
      logo_image: singleAttachmentSelectedImageUrl,
      coordinates: {
        latitude: values?.latitude,
        longitude: values?.longitude,
      },
    };

    dispatch(
      updateProviderProfileAction(
        dataToBeSent,
        navigate,
        setLoading,
        setUserSession
      )
    );
  };



  return (
    <>
      <Flex vertical gap="2rem" className={styles.profileSetupWrapper}>
        <div className={styles.logoContainer}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Button
                type="default"
                size="small"
                icon={<LeftOutlined />}
                onClick={() => navigate(baseRouteMaker(routes.DASHBOARD))}
              >
                {t("CommonUtils.back")}
              </Button>
            </Col>
            <Col span={8}>
              <img src={logo} alt="ReynaCare" />
            </Col>
            <Col span={8} style={{ textAlign: "end" }}>
            <LanguageDropdown width="10px" height="10px" />
            </Col>
          </Row>
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <Row gutter={24} justify="space-between">
            <Col span={10}>
              <Flex
                vertical
                gap="1rem"
                className={styles.profileSetupContainer}
              >
                <Flex gap="1rem" align="center">
                  <Title level={1} style={{ color: colors.primaryColor }}>
                    {t("ProfileSetup.editYourProfile")}
                  </Title>
                </Flex>
                <Title level={4} type="secondary" style={{ margin: 0 }}>
                  {t("ProfileSetup.subTitle")}
                </Title>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onSubmit}
                  autoComplete="off"
                  requiredMark={false}
                >
                  <Flex vertical gap="1rem">
                    <div>
                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("SignUp.nameLabel")}
                            required={true}
                          />
                        }
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: t("SignUp.nameErrorMessage"),
                          },
                          {
                            whitespace: true,
                            message: t("SignUp.nameErrorMessage"),
                          },
                          {
                            validator: (_, value) => validatorFunctionToAllowOnlyEnglish(t("SignUp.nameErrorMessage"))(_,value),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input 
                        placeholder={t("SignUp.namePlaceholder")} 
                        onKeyDown={(e) =>
                          allowAlphaNumericCharacters(e)
                        }
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("SignUp.directorNameLabel")}
                            required={true}
                          />
                        }
                        name="director_name"
                        rules={[
                          {
                            required: true,
                            message: t("SignUp.directorNameErrorMessage"),
                          },
                          {
                            whitespace: true,
                            message: t("SignUp.directorNameErrorMessage"),
                          },
                          {
                            validator: (_, value) => validatorFunctionToAllowOnlyEnglish(t("SignUp.directorNameErrorMessage"))(_,value),
                          }
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t("SignUp.directorNamePlaceholder")}
                          onKeyDown={(e) =>
                            allowAlphaNumericCharacters(e)
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("ProfileSetup.branchLabel")}
                            required={true}
                          />
                        }
                        name="branch"
                        rules={[
                          {
                            required: true,
                            message: t("ProfileSetup.branchErrorMessage"),
                          },
                          {
                            whitespace: true,
                            message: t("ProfileSetup.branchErrorMessage"),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t("ProfileSetup.branchPlaceholder")}
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("SignUp.designationLabel")}
                            required={true}
                          />
                        }
                        name="designation"
                        rules={[
                          {
                            required: true,
                            message: t("SignUp.designationErrorMessage"),
                          },
                          {
                            whitespace: true,
                            message: t("SignUp.designationErrorMessage"),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t("SignUp.designationPlaceholder")}
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("SignUp.emailLabel")}
                            required={true}
                          />
                        }
                        name="email"
                        rules={[
                          {
                            type: t("SignUp.typeEmail"),
                            message: t("SignUp.emailValidation"),
                          },
                          {
                            required: true,
                            message: t("SignUp.emailErrorMessage"),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input placeholder={t("SignUp.emailPlaceholder")} />
                      </Form.Item>

                      <Form.Item
                        initialValue={{
                          code: 971,
                        }}
                        label={
                          <RequiredLabel
                            text={t("SignUp.phoneNumberLabel")}
                            required={true}
                          />
                        }
                        name="phone_number"
                        rules={[
                          {
                            required: true,
                            validator: async (_, value) => {
                              if (value && !value.phone)
                                return Promise.reject(
                                  t("SignUp.phoneNumberErrorMessage")
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <CountryPhoneInput
                          selectProps={{
                            filterOption: (input, option) =>
                              getPhoneInputFilterOptions(input, option),
                          }}
                          maxLength={15}
                          onKeyDown={(e) =>
                            allowCharactersOnlyForIntegerNumbers(e)
                          }
                          inline
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("ProfileSetup.addressLineOneLabel")}
                            required={true}
                          />
                        }
                        name="address_line_1"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "ProfileSetup.addressLineOneErrorMessage"
                            ),
                          },
                          {
                            whitespace: true,
                            message: t(
                              "ProfileSetup.addressLineOneErrorMessage"
                            ),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t(
                            "ProfileSetup.addressLineOnePlaceholder"
                          )}
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("ProfileSetup.addressLineTwoLabel")}
                            required={false}
                          />
                        }
                        name="address_line_2"
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t(
                            "ProfileSetup.addressLineTwoPlaceholder"
                          )}
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("ProfileSetup.countryLabel")}
                            required={true}
                          />
                        }
                        name="country"
                        rules={[
                          {
                            required: true,
                            message: t("ProfileSetup.countryErrorMessage"),
                          },
                        ]}
                      >
                        <Select
                          placeholder={t("ProfileSetup.countryPlaceholder")}
                          popupMatchSelectWidth={true}
                          onChange={(value, element) => {
                            handleCountryChange(value, element);
                          }}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.children ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {countryList?.map((item) => (
                            <Select.Option key={item?._id} value={item?._id}>
                              {item?.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("ProfileSetup.stateProviceLabel")}
                            required={true}
                          />
                        }
                        name="state"
                        rules={[
                          {
                            required: true,
                            message: t("ProfileSetup.stateProviceErrorMessage"),
                          },
                        ]}
                      >
                        <Select
                          placeholder={t(
                            "ProfileSetup.stateProvicePlaceholder"
                          )}
                          popupMatchSelectWidth={true}
                          onChange={(value, element) => {
                            handleStateChange(value, element);
                          }}
                          disabled={!isCountrySelected}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.children ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {stateList?.map((item) => (
                            <Select.Option key={item?.id} value={item?.id}>
                              {item?.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("ProfileSetup.cityLabel")}
                            required={true}
                          />
                        }
                        name="city"
                        rules={[
                          {
                            required: true,
                            message: t("ProfileSetup.cityErrorMessage"),
                          },
                        ]}
                      >
                        <Select
                          placeholder={t("ProfileSetup.cityPlaceholder")}
                          onChange={(value, element) => {
                            handleCityChange(value, element);
                          }}
                          popupMatchSelectWidth={true}
                          disabled={!isStateSelected}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.children ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {cityList?.map((item) => (
                            <Select.Option key={item?.id} value={item?.id}>
                              {item?.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("Modules.Services.ServicesForm.latitude")}
                            required={true}
                          />
                        }
                        name="latitude"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Services.ServicesForm.pleaseEnterLatitude"
                            ),
                          },
                          {
                            validator: (_, value) => {
                              if (value && value < 1)
                                return Promise.reject(
                                  `${t(
                                    "Modules.Services.ServicesForm.latitude"
                                  )} ${t("CommonUtils.shouldBeGreaterThan0")}`
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          placeholder={t(
                            "Modules.Services.ServicesForm.enterLatitude"
                          )}
                          onKeyDown={(e) =>
                            allowCharactersOnlyForDecimalNumbers(e)
                          }
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("Modules.Services.ServicesForm.longitude")}
                            required={true}
                          />
                        }
                        name="longitude"
                        rules={[
                          {
                            required: true,
                            message: t(
                              "Modules.Services.ServicesForm.pleaseEnterLongitude"
                            ),
                          },
                          {
                            validator: (_, value) => {
                              if (value && value < 1)
                                return Promise.reject(
                                  `${t(
                                    "Modules.Services.ServicesForm.longitude"
                                  )} ${t("CommonUtils.shouldBeGreaterThan0")}`
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          placeholder={t(
                            "Modules.Services.ServicesForm.enterLongitude"
                          )}
                          onKeyDown={(e) =>
                            allowCharactersOnlyForDecimalNumbers(e)
                          }
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <RequiredLabel
                            text={t("ProfileSetup.zipPostalLabel")}
                            required={true}
                          />
                        }
                        name="zip_code"
                        rules={[
                          {
                            required: true,
                            message: t("ProfileSetup.zipPostalErrorMessage"),
                          },
                          {
                            whitespace: true,
                            message: t("ProfileSetup.zipPostalErrorMessage"),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          style={{ width: "100%" }}
                          controls={false}
                          type="number"
                          placeholder={t("ProfileSetup.zipPostalPlaceholder")}
                          onKeyDown={allowCharactersOnlyForDecimalNumbers}
                          onWheel={disableOnWheel}
                        />
                      </Form.Item>
                    </div>

                    <Flex vertical gap="1rem" align="center" justify="center">
                      <Button
                        type="primary"
                        htmlType="submit"
                        style={{ width: "100%" }}
                        loading={loading}
                      >
                        {t("ProfileSetup.updateProfile")}
                      </Button>
                    </Flex>
                  </Flex>
                </Form>
              </Flex>
            </Col>
            <Col span={12}>
            <Flex vertical gap="2rem" align="center">
              <Flex vertical align="center" style={{ position: "relative" }}>
                <FileAttachment
                  attachmentObj={singleAttachmentObj}
                  attachmentUrl={singleAttachmentSelectedImageUrl}
                  loading={imageLoader}
                />
                <div
                  className="attachmentContainer"
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    bottom: "-25px",
                    background:colors.turquoiseGreen,
                    borderRadius:10,
                
                  }}
                  onClick={() => attachmentRef.current?.click()}
                >
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    style={{ background:colors.turquoiseGreen}}
                    icon={<CameraOutlined style={{ fontSize: 22 }} />}
                  />
                </div>
                <input
                  type="file"
                  style={{ display: "none" }}
                  ref={attachmentRef}
                  accept="image/*"
                  onChange={onImageChanged}
                />
              </Flex>
              <Typography.Title level={3} type="secondary" style={{ marginBottom: 10 }}>
                {t("CommonUtils.supportedImageFormats")}
              </Typography.Title>
              </Flex>
            </Col>
          </Row>
        )}
      </Flex>
    </>
  );
};

export default UpdateProfileSetup;
